import { type TransactionQuery, TransactionService } from "@/service";
import type { TTransaction } from "@/types";
import { enableMapSet } from "immer";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

enableMapSet();

type TTransactionStore = {
   workspaceTransactionMap: Record<string, Map<number, TTransaction>>;
   fetchTransactions: (
      slug: string,
      query: Omit<TransactionQuery, "memberId">,
   ) => Promise<TTransaction[]>;
   fetchTransaction: (
      slug: string,
      id: number | string,
   ) => Promise<TTransaction>;
   createTransaction(
      slug: string,
      data: Pick<TTransaction, "amount" | "method" | "reason" | "type">,
   ): Promise<TTransaction>;
   updateTransaction(
      slug: string,
      id: number,
      data: Partial<Pick<TTransaction, "reason" | "status">>,
   ): Promise<TTransaction>;
};

const service = new TransactionService();

export const useTransactionStore = create<TTransactionStore>()(
   immer((set) => ({
      workspaceTransactionMap: {},
      fetchTransactions: async (slug, query) => {
         const transactions = await service.getTransactions(slug, query);
         set((store) => {
            store.workspaceTransactionMap[slug] ??= new Map();
            transactions.forEach((transaction) => {
               store.workspaceTransactionMap[slug].set(
                  transaction.id,
                  transaction,
               );
            });
         });
         return transactions;
      },
      fetchTransaction: async (slug, id) => {
         return service.getTransaction(slug, id).then((response) => {
            set((store) => {
               store.workspaceTransactionMap[slug] ??= new Map();

               store.workspaceTransactionMap[slug].set(response.id, response);
            });
            return response;
         });
      },
      createTransaction: async (slug, data) => {
         return service.createTransaction(slug, data).then((response) => {
            set((store) => {
               store.workspaceTransactionMap[slug] ??= new Map();
               store.workspaceTransactionMap[slug].set(response.id, response);
            });
            return response;
         });
      },
      updateTransaction: async (slug, id, data) => {
         return service.updateTransaction(slug, id, data).then((response) => {
            set((store) => {
               store.workspaceTransactionMap[slug] ??= new Map();
               store.workspaceTransactionMap[slug].set(id, response);
            });
            return response;
         });
      },
   })),
);
